import { useEffect, useState } from "react";
import { FileText } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { deleteItem } from "@/lib/deleteItem";
import { Skel } from "@/components/app/Common";

export function ResumeViewer({ candidate }) {
  const qc = useQueryClient();
  const { user } = useAuth();
  const staff = ["super_admin", "recruiter"].includes(user?.role);
  const [url, setUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [mode, setMode] = useState("original");
  const isPdf = candidate.resume_content_type === "application/pdf";
  const showPdf = isPdf && !failed && mode === "original";

  useEffect(() => {
    if (!isPdf || !candidate.resume_file_id) return;
    let objectUrl;
    api.get(`/candidates/${candidate.id}/resume`, { responseType: "blob" })
      .then((r) => { objectUrl = URL.createObjectURL(r.data); setUrl(objectUrl); })
      .catch(() => setFailed(true));
    return () => objectUrl && URL.revokeObjectURL(objectUrl);
  }, [candidate.id, candidate.resume_file_id, isPdf]);

  const download = async () => {
    const r = await api.get(`/candidates/${candidate.id}/resume`, { responseType: "blob" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(r.data);
    a.download = candidate.resume_filename || "resume";
    a.click();
  };

  return (
    <section className="panel flex h-full flex-col overflow-hidden" data-tour="pt-resume-viewer" data-testid="resume-viewer">
      <div className="flex items-center justify-between max-sm:flex-wrap max-sm:gap-2 border-b border-line px-5 py-3">
        <p className="flex items-center gap-2 truncate text-sm font-medium"><FileText size={16} className="text-ink-muted" /> {candidate.resume_filename || "No file"}</p>
        <div className="flex items-center gap-3">
          {isPdf && candidate.resume_text && (
            <button onClick={() => setMode(mode === "original" ? "text" : "original")} className="text-sm text-ink-secondary hover:text-ink" data-tour="pt-resume-mode-toggle" data-testid="resume-mode-toggle">
              {mode === "original" ? "Extracted text" : "Original PDF"}
            </button>
          )}
          {candidate.resume_file_id && <button onClick={download} className="text-sm text-brand hover:underline" data-tour="pt-resume-download-btn" data-testid="resume-download-btn">Download</button>}
          {candidate.resume_file_id && staff && <button onClick={() => deleteItem({ title: "Delete the resume file?", entity: candidate.resume_filename || "Resume",
            body: "The uploaded file is deleted from storage. The details already read from it stay on the profile.", url: `/candidates/${candidate.id}/resume`, success: "Resume file deleted",
            onDone: () => { qc.invalidateQueries({ queryKey: ["candidate", candidate.id] }); } })} className="text-sm text-ink-muted hover:text-risk" data-testid="resume-delete-btn">Delete file</button>}
        </div>
      </div>
      {showPdf ? (
        url ? <iframe title="Resume" src={url} className="h-[75vh] w-full" data-testid="resume-pdf-frame" /> : <Skel className="m-5 h-[60vh]" />
      ) : (
        <div className="h-[75vh] overflow-y-auto p-5" data-testid="resume-text">
          {candidate.resume_text
            ? <p className="whitespace-pre-wrap text-sm leading-relaxed text-ink-secondary">{candidate.resume_text}</p>
            : <p className="text-sm text-ink-muted">We couldn't extract text from this file. Download it to view the original.</p>}
        </div>
      )}
    </section>
  );
}
